<template>
    <div class="malt-strategy-high-level-banner" :class="{reduced}" data-testid="strategy-high-level-banner">
        <div class="malt-strategy-high-level-banner__content">
            <MaltImg src="/malt-logos/malt_strategy_logo.svg" alt="Malt Strategy" height="34" />

            <div class="malt-strategy-high-level-banner__text">
                <h2 class="malt-strategy-high-level-banner__title">
                    {{ $t('search.banner.malt.strategy.high.level.title') }}
                </h2>
                <p v-show="!reduced" class="malt-strategy-high-level-banner__description">
                    {{ $t('search.banner.malt.strategy.high.level.text') }}
                </p>
            </div>

            <VJoyButton
                class="malt-strategy-high-level-banner__link"
                icon-position="right"
                icon="arrow-right"
                :href="urlToAdvancedSearch"
                data-testid="strategy-high-level-banner-cta"
            >
                {{ $t('search.banner.malt.strategy.high.level.cta') }}
            </VJoyButton>

            <LinkButton
                v-show="!reduced"
                class="malt-strategy-high-level-banner__info"
                data-testid="strategy-high-level-banner-learn-more-cta"
                @click="navigateTo('/c/maltstrategy/companies', {external: true})"
            >
                {{ $t('search.banner.malt.strategy.high.level.info.text') }}
            </LinkButton>
        </div>

        <div class="malt-strategy-high-level-banner__illustration"></div>

        <VJoyButton
            v-if="reduced"
            class="malt-strategy-high-level-banner__collapse-button"
            data-testid="strategy-high-level-banner-expand-btn"
            variant="ghost"
            size="small"
            circle
            icon="add"
            @click="emit('expand')"
        />

        <VJoyButton
            v-else
            class="malt-strategy-high-level-banner__collapse-button"
            data-testid="strategy-high-level-banner-reduce-btn"
            variant="ghost"
            size="small"
            circle
            icon="minus"
            @click="emit('reduce')"
        />
    </div>
</template>

<script setup lang="ts">
    import {MaltImg} from '#components';
    import {navigateTo} from '#imports';
    import {VJoyButton} from '@maltjoy/core-vue';
    import LinkButton from '~/components/common/LinkButton.vue';
    import {useLinksFrom} from '../useLinks';

    defineProps<{
        reduced: boolean;
    }>();

    const {urlToAdvancedSearch} = useLinksFrom('search_banner_strategy_high_level');

    const emit = defineEmits<{
        (e: 'reduce'): void;
        (e: 'expand'): void;
    }>();
</script>

<style scoped lang="scss">
    @use '@malt/joy-entrypoint' as joy;

    // desktop & expanded banner style
    .malt-strategy-high-level-banner {
        position: relative;
        display: flex;
        justify-content: space-between;
        align-items: stretch;
        border-radius: var(--joy-core-radius-4);
        box-shadow: var(--joy-core-elevation-1);
        overflow: hidden;
        margin: var(--joy-core-spacing-6);

        * {
            transition: all var(--joy-transition-duration-default) var(--joy-transition-timing-function);
        }

        &__content {
            position: relative;
            width: 100%;
            max-width: 540px;
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            flex: auto;
            background: var(--neutral-joy-color-neutral-white);
            padding: var(--joy-core-spacing-12) var(--joy-core-spacing-10) var(--joy-core-spacing-12) var(--joy-core-spacing-10);
            gap: var(--joy-core-spacing-10);
        }

        &__title {
            font-size: var(--joy-font-size-secondary-400);
            font-family: var(--joy-font-family-title);
            line-height: 1.1;
            margin-bottom: var(--joy-core-spacing-2);
        }

        &__description {
            font-size: var(--joy-font-size-primary-600);
        }

        &__illustration {
            flex-grow: 1;
            width: 100%;
            background: no-repeat 0 70% / cover
                url('https://dam.malt.com/hopsearch/banner-malt-strategy-high-level/search-banner-strategy-high-level?w=1400');
            clip-path: polygon(0 0, 100% 0%, 100% 100%, 110px 100%);
        }

        &__collapse-button {
            position: absolute;
            top: var(--joy-core-spacing-6);
            right: var(--joy-core-spacing-6);
        }
    }

    // adjustments for desktop 'reduced' banner
    .reduced {
        .malt-strategy-high-level-banner {
            &__content {
                // compact content
                padding: var(--joy-core-spacing-6) var(--joy-core-spacing-10) var(--joy-core-spacing-6) var(--joy-core-spacing-10);
                gap: var(--joy-core-spacing-4);
            }

            &__logo {
                // smaller Malt Strategy logo
                height: 24px;
            }

            &__title {
                // smaller title
                font-size: var(--joy-font-size-secondary-200);
            }

            &__description,
            &__info {
                display: none;
            }
        }
    }

    // adjustments for 'expanded' banner on mobile screens
    @include joy.screen_sm {
        .malt-strategy-high-level-banner {
            // use a vertical layout
            flex-direction: column-reverse;

            &__content {
                // compact content for mobile devices
                padding: var(--joy-core-spacing-9) var(--joy-core-spacing-6) var(--joy-core-spacing-9);
                gap: var(--joy-core-spacing-6);
                // content can use the entire screen width
                max-width: unset;
            }

            &__logo {
                display: none;
            }

            &__title {
                font-size: var(--joy-font-size-primary-800);
                font-family: inherit;
            }

            &__description {
                display: block;
            }

            &__info {
                display: none;
            }

            &__illustration {
                height: 278px;
                flex-shrink: 0;
                clip-path: unset;
                background-position: 50%;
            }
        }
    }

    // adjustments for 'reduced' banner on mobile screens
    @include joy.screen_sm {
        .reduced {
            .malt-strategy-high-level-banner {
                &__content {
                    // compact content for mobile devices
                    padding: var(--joy-core-spacing-6);
                    gap: var(--joy-core-spacing-6);
                }

                &__title {
                    font-size: var(--joy-font-size-primary-800);
                    font-family: inherit;
                }

                &__illustration {
                    height: 80px;
                }
            }
        }
    }
</style>
